/* eslint-disable no-console */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Slider from '@mui/material/Slider';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';

import {
  fetchApiRequest,
  getApiHost,
  getAuthHeaders,
  getLocationParam,
  loadEvents,
  renderBoilerPlate,
} from './Common';
import {
  audioBufferToWavBlob,
  audacityColor,
  buildTrimmedBuffer,
  decodeAudioData,
  renderSpectrogramToCanvas,
} from './analysisShared';

type AudioSourceInput = Blob | string;

type PlaybackDebugState = {
  duration: number;
  currentTime: number;
  readyState: number;
  networkState: number;
  paused: boolean;
  ended: boolean;
  lastEvent: string;
  errorCode: number;
  errorMessage: string;
};

type EventOption = {
  id: number;
  name: string;
};

const ANALYSIS_EXTRA_EVENTS = ['Fpv', 'Hexacopter', 'Quadcopter', 'Fixed Wing'];

const FFT_SIZE = 4096;
const MIN_TRIM_MS = 0;

function asSliderValue(value: number | number[]): number {
  return Array.isArray(value) ? value[0] : value;
}

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds)) return '--';
  return `${seconds.toFixed(2)} s`;
}

function Analysis() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationRef = useRef<number | null>(null);
  const mediaSourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const highpassNodeRef = useRef<BiquadFilterNode | null>(null);
  const lowpassNodeRef = useRef<BiquadFilterNode | null>(null);
  const graphAudioElementRef = useRef<HTMLAudioElement | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recorderChunksRef = useRef<Blob[]>([]);
  const currentObjectUrlRef = useRef<string | null>(null);
  const staticCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const staticBufferCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const boostedStaticBufferCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const dragStateRef = useRef({ dragging: false, x: 0, y: 0 });
  const autoBuildDetailedSpecRef = useRef(false);

  const initialEventUrl = getLocationParam('audio_url') || '';
  const [audioUrl, setAudioUrl] = useState('');
  const [eventUrl, setEventUrl] = useState(initialEventUrl);
  const [annotationLoadFormat, setAnnotationLoadFormat] = useState('mp3');
  const [sourceLabel, setSourceLabel] = useState('No source loaded');
  const [audioBuffer, setAudioBuffer] = useState<AudioBuffer | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [gain, setGain] = useState(1);
  const [noiseGateDb, setNoiseGateDb] = useState(-70);
  const [freqMinHz, setFreqMinHz] = useState(0);
  const [freqMaxHz, setFreqMaxHz] = useState(6000);
  const [trimStartMs, setTrimStartMs] = useState(0);
  const [trimEndMs, setTrimEndMs] = useState(0);
  const [customName, setCustomName] = useState('');
  const [annotationLabels, setAnnotationLabels] = useState<Record<string, boolean>>({});
  const [availableEvents, setAvailableEvents] = useState<EventOption[]>([]);
  const [uploadDeviceId, setUploadDeviceId] = useState('');
  const [saveToLocalHostFolder, setSaveToLocalHostFolder] = useState(false);
  const [localExportSubpath, setLocalExportSubpath] = useState('');
  const [customLabelDraft, setCustomLabelDraft] = useState('');
  const [customLabelNames, setCustomLabelNames] = useState<string[]>([]);
  const customLabelNamesRef = useRef<string[]>([]);
  customLabelNamesRef.current = customLabelNames;
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [currentBatchIndex, setCurrentBatchIndex] = useState(-1);
  const [currentUploadFileName, setCurrentUploadFileName] = useState('');
  const [liveSpectrogramEnabled, setLiveSpectrogramEnabled] = useState(true);
  const [isBuildingStaticSpec, setIsBuildingStaticSpec] = useState(false);
  const [hasStaticSpec, setHasStaticSpec] = useState(false);
  const [showBoostedDetailedSpec, setShowBoostedDetailedSpec] = useState(false);
  const [zoomX, setZoomX] = useState(1);
  const [zoomY, setZoomY] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [playbackDebug, setPlaybackDebug] = useState<PlaybackDebugState>({
    duration: 0,
    currentTime: 0,
    readyState: 0,
    networkState: 0,
    paused: true,
    ended: false,
    lastEvent: 'none',
    errorCode: 0,
    errorMessage: '',
  });

  const durationSec = useMemo(() => (audioBuffer ? audioBuffer.duration : 0), [audioBuffer]);
  const activeBatchFile = useMemo(
    () => (
      currentBatchIndex >= 0 && currentBatchIndex < uploadedFiles.length
        ? uploadedFiles[currentBatchIndex]
        : null
    ),
    [currentBatchIndex, uploadedFiles],
  );
  const maxTrimMs = useMemo(
    () => Math.max(0, Math.floor(durationSec * 1000) - 1),
    [durationSec],
  );
  const staticSpecMargins = useMemo(
    () => ({
      left: 64,
      right: 14,
      top: 12,
      bottom: 42,
    }),
    [],
  );

  const clearStatus = () => {
    setError('');
    setMessage('');
  };

  const applyAnnotationLabelChange = useCallback((eventName: string, checked: boolean) => {
    setAnnotationLabels((prev) => {
      const next = { ...prev };
      if (eventName === 'Casual' && checked) {
        Object.keys(next).forEach((label) => {
          next[label] = false;
        });
      } else if (eventName !== 'Casual' && checked && next.Casual) {
        next.Casual = false;
      }
      next[eventName] = checked;
      return next;
    });
  }, []);

  const addCustomAnnotationLabel = () => {
    const raw = customLabelDraft.trim().replace(/\s+/g, ' ');
    if (!raw) {
      setError('Enter a non-empty custom label.');
      return;
    }
    if (raw.length > 120) {
      setError('Custom label is too long (max 120 characters).');
      return;
    }
    const existing = new Set([
      ...availableEvents.map((e) => e.name),
      ...customLabelNames,
    ]);
    if (existing.has(raw)) {
      setError(`Label "${raw}" is already in the list.`);
      return;
    }
    clearStatus();
    setCustomLabelNames((prev) => [...prev, raw]);
    setAnnotationLabels((prev) => ({ ...prev, [raw]: false }));
    setCustomLabelDraft('');
  };

  const removeCustomAnnotationLabel = (name: string) => {
    setCustomLabelNames((prev) => prev.filter((n) => n !== name));
    setAnnotationLabels((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  useEffect(() => {
    loadEvents((events: EventOption[]) => {
      const existing = new Set(events.map((event) => event.name));
      const extraEvents: EventOption[] = ANALYSIS_EXTRA_EVENTS
        .filter((name) => !existing.has(name))
        .map((name, index) => ({ id: 10000 + index, name }));
      const mergedEvents = [...events, ...extraEvents];
      setAvailableEvents(mergedEvents);
      setAnnotationLabels((prev) => {
        const base = Object.fromEntries(
          mergedEvents
            .filter((event) => event.name !== 'Ignore')
            .map((event) => [event.name, false]),
        );
        customLabelNamesRef.current.forEach((name) => {
          if (!Object.prototype.hasOwnProperty.call(base, name)) {
            base[name] = prev[name] ?? false;
          }
        });
        return base;
      });
    });
  }, []);

  const drawStaticViewport = useCallback(() => {
    const display = staticCanvasRef.current;
    const source = showBoostedDetailedSpec
      ? boostedStaticBufferCanvasRef.current
      : staticBufferCanvasRef.current;
    if (!display || !source) return;
    const ctx = display.getContext('2d');
    if (!ctx) return;
    const srcW = Math.max(10, Math.floor(source.width / zoomX));
    const srcH = Math.max(10, Math.floor(source.height / zoomY));
    const maxX = Math.max(0, source.width - srcW);
    const maxY = Math.max(0, source.height - srcH);
    const clampedX = Math.max(0, Math.min(maxX, panX));
    const clampedY = Math.max(0, Math.min(maxY, panY));
    const plotX = staticSpecMargins.left;
    const plotY = staticSpecMargins.top;
    const plotW = Math.max(40, display.width - staticSpecMargins.left - staticSpecMargins.right);
    const plotH = Math.max(40, display.height - staticSpecMargins.top - staticSpecMargins.bottom);
    const startTimeSec = (clampedX / source.width) * durationSec;
    const endTimeSec = ((clampedX + srcW) / source.width) * durationSec;
    const nyquist = audioBuffer ? audioBuffer.sampleRate / 2 : Math.max(freqMaxHz, 1);
    const startFreqHz = nyquist * (1 - ((clampedY + srcH) / source.height));
    const endFreqHz = nyquist * (1 - (clampedY / source.height));
    const xTicks = 6;
    const yTicks = 5;
    ctx.clearRect(0, 0, display.width, display.height);
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, display.width, display.height);
    ctx.drawImage(source, clampedX, clampedY, srcW, srcH, plotX, plotY, plotW, plotH);

    ctx.strokeStyle = '#9aa0a6';
    ctx.lineWidth = 1;
    ctx.strokeRect(plotX, plotY, plotW, plotH);
    ctx.fillStyle = '#d8dee9';
    ctx.font = '12px sans-serif';

    for (let i = 0; i <= xTicks; i += 1) {
      const ratio = i / xTicks;
      const x = plotX + (ratio * plotW);
      const tickTime = startTimeSec + ((endTimeSec - startTimeSec) * ratio);
      ctx.beginPath();
      ctx.moveTo(x, plotY + plotH);
      ctx.lineTo(x, plotY + plotH + 6);
      ctx.stroke();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(`${tickTime.toFixed(2)}s`, x, plotY + plotH + 8);
    }

    for (let i = 0; i <= yTicks; i += 1) {
      const ratio = i / yTicks;
      const y = plotY + ((1 - ratio) * plotH);
      const tickFreq = startFreqHz + ((endFreqHz - startFreqHz) * ratio);
      ctx.beginPath();
      ctx.moveTo(plotX - 6, y);
      ctx.lineTo(plotX, y);
      ctx.stroke();
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${Math.round(tickFreq)} Hz`, plotX - 10, y);
    }
  }, [
    audioBuffer,
    durationSec,
    freqMaxHz,
    panX,
    panY,
    showBoostedDetailedSpec,
    staticSpecMargins,
    zoomX,
    zoomY,
  ]);

  useEffect(() => {
    drawStaticViewport();
  }, [drawStaticViewport]);

  useEffect(() => {
    const canvas = staticCanvasRef.current;
    if (!canvas) return undefined;

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      const delta = event.deltaY > 0 ? 0.9 : 1.1;
      if (event.shiftKey) {
        setZoomY((v) => Math.max(1, Math.min(20, v * delta)));
      } else {
        setZoomX((v) => Math.max(1, Math.min(20, v * delta)));
      }
    };

    canvas.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      canvas.removeEventListener('wheel', handleWheel);
    };
  }, []);

  const ensureAudioGraph = useCallback(() => {
    const element = audioRef.current;
    if (!element) return;
    const needsRebuild =
      !audioContextRef.current || graphAudioElementRef.current !== element;
    if (needsRebuild) {
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
      }
      const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
      audioContextRef.current = new AudioContextCtor();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = FFT_SIZE;
      gainNodeRef.current = audioContextRef.current.createGain();
      gainNodeRef.current.gain.value = gain;
      highpassNodeRef.current = audioContextRef.current.createBiquadFilter();
      highpassNodeRef.current.type = 'highpass';
      highpassNodeRef.current.frequency.value = Math.max(0, freqMinHz);
      lowpassNodeRef.current = audioContextRef.current.createBiquadFilter();
      lowpassNodeRef.current.type = 'lowpass';
      lowpassNodeRef.current.frequency.value = Math.max(freqMinHz + 1, freqMaxHz);
      mediaSourceRef.current = audioContextRef.current.createMediaElementSource(element);
      mediaSourceRef.current.connect(gainNodeRef.current);
      gainNodeRef.current.connect(highpassNodeRef.current);
      highpassNodeRef.current.connect(lowpassNodeRef.current);
      lowpassNodeRef.current.connect(analyserRef.current);
      analyserRef.current.connect(audioContextRef.current.destination);
      graphAudioElementRef.current = element;
    }
  }, [freqMaxHz, freqMinHz, gain]);

  const startSpectrogram = useCallback(() => {
    const analyser = analyserRef.current;
    const canvas = canvasRef.current;
    if (!analyser || !canvas) return;
    if (animationRef.current) {
      window.cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    const canvasCtx = canvas.getContext('2d', { willReadFrequently: true });
    if (!canvasCtx) return;
    const frequencyData = new Uint8Array(analyser.frequencyBinCount);
    const width = canvas.width;
    const height = canvas.height;

    const draw = () => {
      analyser.getByteFrequencyData(frequencyData);
      canvasCtx.drawImage(canvas, 1, 0, width - 1, height, 0, 0, width - 1, height);
      for (let y = 0; y < height; y += 1) {
        const frequencyIndex = Math.floor((1 - y / height) * (frequencyData.length - 1));
        const nyquist = (audioContextRef.current?.sampleRate || 48000) / 2;
        const currentHz = (frequencyIndex / (frequencyData.length - 1)) * nyquist;
        if (currentHz < freqMinHz || currentHz > freqMaxHz) {
          canvasCtx.fillStyle = 'rgb(0, 0, 0)';
          canvasCtx.fillRect(width - 1, y, 1, 1);
          // eslint-disable-next-line no-continue
          continue;
        }
        const amp = frequencyData[frequencyIndex] / 255;
        const db = 20 * Math.log10(amp + 1e-12);
        const gated = db >= noiseGateDb ? amp : 0;
        const { r, g, b } = audacityColor(gated);
        canvasCtx.fillStyle = `rgb(${r}, ${g}, ${b})`;
        canvasCtx.fillRect(width - 1, y, 1, 1);
      }
      animationRef.current = window.requestAnimationFrame(draw);
    };

    draw();
  }, [freqMaxHz, freqMinHz, noiseGateDb]);

  const stopSpectrogram = useCallback(() => {
    if (animationRef.current) {
      window.cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  }, []);

  useEffect(() => {
    const syncLiveSpectrogramState = async () => {
      const element = audioRef.current;
      if (!element) return;
      if (!liveSpectrogramEnabled) {
        stopSpectrogram();
        return;
      }
      if (element.paused || element.ended) return;
      ensureAudioGraph();
      if (audioContextRef.current?.state === 'suspended') {
        await audioContextRef.current.resume();
      }
      startSpectrogram();
    };
    void syncLiveSpectrogramState();
  }, [audioUrl, ensureAudioGraph, liveSpectrogramEnabled, startSpectrogram, stopSpectrogram]);

  useEffect(() => {
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = gain;
    }
  }, [gain]);

  useEffect(() => {
    if (highpassNodeRef.current) {
      highpassNodeRef.current.frequency.value = Math.max(0, freqMinHz);
    }
    if (lowpassNodeRef.current) {
      lowpassNodeRef.current.frequency.value = Math.max(freqMinHz + 1, freqMaxHz);
    }
  }, [freqMaxHz, freqMinHz]);

  useEffect(
    () => () => {
      stopSpectrogram();
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop();
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    },
    [stopSpectrogram],
  );

  const attachAudioSource = useCallback(async (blobOrUrl: AudioSourceInput, label: string) => {
    clearStatus();
    setIsLoading(true);
    try {
      let arrayBuffer: ArrayBuffer;
      let sourceContentType = 'audio/wav';
      if (typeof blobOrUrl === 'string') {
        const response = await fetch(blobOrUrl, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) {
          throw new Error(`Failed to load audio (${response.status})`);
        }
        arrayBuffer = await response.arrayBuffer();
        sourceContentType = response.headers.get('content-type') || 'audio/wav';
      } else {
        arrayBuffer = await blobOrUrl.arrayBuffer();
        sourceContentType = blobOrUrl.type || 'audio/wav';
      }
      const decoded = await decodeAudioData(arrayBuffer);
      const playbackBlob = audioBufferToWavBlob(decoded);
      const newAudioUrl = URL.createObjectURL(playbackBlob);
      if (currentObjectUrlRef.current) {
        URL.revokeObjectURL(currentObjectUrlRef.current);
      }
      currentObjectUrlRef.current = newAudioUrl;
      autoBuildDetailedSpecRef.current = true;
      setAudioBuffer(decoded);
      setAudioUrl(newAudioUrl);
      setSourceLabel(label);
      setTrimStartMs(0);
      setTrimEndMs(0);
      setHasStaticSpec(false);
      setMessage(`Audio loaded and normalized for playback (${sourceContentType} -> wav).`);
    } catch (loadError: any) {
      setError(loadError.message || String(loadError));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(
    () => () => {
      if (currentObjectUrlRef.current) {
        URL.revokeObjectURL(currentObjectUrlRef.current);
        currentObjectUrlRef.current = null;
      }
    },
    [],
  );

  const buildDetailedSpectrogram = useCallback(() => {
    if (!audioBuffer) return;
    if (!staticBufferCanvasRef.current) {
      staticBufferCanvasRef.current = document.createElement('canvas');
    }
    if (!boostedStaticBufferCanvasRef.current) {
      boostedStaticBufferCanvasRef.current = document.createElement('canvas');
    }
    setIsBuildingStaticSpec(true);
    window.setTimeout(() => {
      try {
        renderSpectrogramToCanvas(audioBuffer, staticBufferCanvasRef.current as HTMLCanvasElement, {
          gainMultiplier: 1,
          noiseGateDb,
          minFreqHz: freqMinHz,
          maxFreqHz: freqMaxHz,
        });
        renderSpectrogramToCanvas(
          audioBuffer,
          boostedStaticBufferCanvasRef.current as HTMLCanvasElement,
          {
            gainMultiplier: gain,
            noiseGateDb,
            minFreqHz: freqMinHz,
            maxFreqHz: freqMaxHz,
          },
        );
        setZoomX(1);
        setZoomY(1);
        setPanX(0);
        setPanY(0);
        setHasStaticSpec(true);
        // Paint immediately so user doesn't need to interact (zoom/pan)
        // before seeing the generated spectrogram.
        window.requestAnimationFrame(() => {
          drawStaticViewport();
        });
      } catch (specError: any) {
        setError(specError.message || String(specError));
      } finally {
        setIsBuildingStaticSpec(false);
      }
    }, 0);
  }, [audioBuffer, drawStaticViewport, freqMaxHz, freqMinHz, gain, noiseGateDb]);

  useEffect(() => {
    if (!audioBuffer || !autoBuildDetailedSpecRef.current) return;
    autoBuildDetailedSpecRef.current = false;
    buildDetailedSpectrogram();
  }, [audioBuffer, buildDetailedSpectrogram]);

  const updatePlaybackDebug = useCallback((eventName: string) => {
    const element = audioRef.current;
    if (!element) return;
    const errorCode = element.error ? element.error.code : 0;
    const errorMessage = element.error ? (element.error.message || `MediaError ${errorCode}`) : '';
    setPlaybackDebug({
      duration: Number.isFinite(element.duration) ? element.duration : 0,
      currentTime: element.currentTime || 0,
      readyState: element.readyState,
      networkState: element.networkState,
      paused: element.paused,
      ended: element.ended,
      lastEvent: eventName,
      errorCode,
      errorMessage,
    });
  }, []);

  const loadFromAnnotation = useCallback(() => {
    clearStatus();
    const url = eventUrl.trim();
    if (!url) {
      setError('Provide an annotation event URL first.');
      return;
    }
    getApiHost((apiHost: string) => {
      const proxyUrl =
        `${apiHost}/v1/audio?audio_url=${encodeURIComponent(url)}&audio_format=${annotationLoadFormat}`;
      setUploadedFiles([]);
      setCurrentBatchIndex(-1);
      setCurrentUploadFileName('');
      attachAudioSource(proxyUrl, `Annotation clip: ${url}`);
    });
  }, [annotationLoadFormat, attachAudioSource, eventUrl]);

  useEffect(() => {
    if (initialEventUrl) {
      loadFromAnnotation();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getClipNameFromFile = (file: File): string => file.name.replace(/\.[^/.]+$/, '');
  const extractDeviceIdFromFileName = (fileName: string): string => {
    if (!fileName) return '';
    const didMatch = fileName.match(/(?:^|,)DID:([^,]+)/i);
    if (didMatch && didMatch[1]) return didMatch[1].trim();
    const plainDidMatch = fileName.match(/(?:^|[_-])did[_-]?([A-Za-z0-9._-]+)/i);
    if (plainDidMatch && plainDidMatch[1]) return plainDidMatch[1].trim();
    const baseName = fileName.replace(/\.[^/.]+$/, '');
    const tokens = baseName.split(/[^A-Za-z0-9-]+/).filter(Boolean);
    const orgStyleToken = tokens.find((token) =>
      /^[A-Za-z]+-[A-Za-z0-9]+(?:-[A-Za-z0-9]+)?$/.test(token));
    if (orgStyleToken) return orgStyleToken;
    return '';
  };

  const loadBatchFileAtIndex = useCallback(async (index: number) => {
    if (index < 0 || index >= uploadedFiles.length) return;
    const file = uploadedFiles[index];
    setCurrentBatchIndex(index);
    setCurrentUploadFileName(file.name);
    setCustomName(getClipNameFromFile(file));
    await attachAudioSource(file, `Batch file ${index + 1}/${uploadedFiles.length}: ${file.name}`);
  }, [attachAudioSource, uploadedFiles]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    if (files.length === 1) {
      const [singleFile] = files;
      setUploadedFiles([]);
      setCurrentBatchIndex(-1);
      setCurrentUploadFileName(singleFile.name);
      setCustomName(getClipNameFromFile(singleFile));
      await attachAudioSource(singleFile, `Uploaded file: ${singleFile.name}`);
    } else {
      setUploadedFiles(files);
      setCurrentBatchIndex(0);
      setCurrentUploadFileName(files[0].name);
      setCustomName(getClipNameFromFile(files[0]));
      await attachAudioSource(files[0], `Batch file 1/${files.length}: ${files[0].name}`);
      setMessage(`Loaded batch with ${files.length} files. Save + next to annotate faster.`);
    }
    event.target.value = '';
  };

  const startRecording = async () => {
    clearStatus();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recorderChunksRef.current = [];
      recorderRef.current = new MediaRecorder(stream);
      recorderRef.current.ondataavailable = (evt: BlobEvent) => {
        if (evt.data.size > 0) recorderChunksRef.current.push(evt.data);
      };
      recorderRef.current.onstop = async () => {
        const blob = new Blob(recorderChunksRef.current, {
          type: recorderRef.current?.mimeType || 'audio/webm',
        });
        setUploadedFiles([]);
        setCurrentBatchIndex(-1);
        setCurrentUploadFileName('');
        await attachAudioSource(blob, 'Recorded in analysis page');
        stream.getTracks().forEach((track) => track.stop());
      };
      recorderRef.current.start();
      setIsRecording(true);
      setMessage('Recording started.');
    } catch (recordError: any) {
      setError(recordError.message || String(recordError));
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
    setIsRecording(false);
  };

  const buildTrimmedBlob = () => {
    if (!audioBuffer) throw new Error('No audio loaded.');
    const trimmedBuffer = buildTrimmedBuffer(audioBuffer, trimStartMs, trimEndMs);
    return audioBufferToWavBlob(trimmedBuffer);
  };

  const downloadTrimmedClip = () => {
    clearStatus();
    try {
      const trimmedBlob = buildTrimmedBlob();
      const name = (customName || 'annotated-clip').replace(/\s+/g, '-');
      const downloadUrl = URL.createObjectURL(trimmedBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${name}.wav`;
      link.click();
      URL.revokeObjectURL(downloadUrl);
      setMessage('Trimmed clip downloaded.');
    } catch (trimError: any) {
      setError(trimError.message || String(trimError));
    }
  };

  const saveAnnotatedClip = async (): Promise<boolean> => {
    clearStatus();
    let trimmedBlob: Blob;
    try {
      trimmedBlob = buildTrimmedBlob();
    } catch (trimError: any) {
      setError(trimError.message || String(trimError));
      return false;
    }

    if (saveToLocalHostFolder) {
      setUploading(true);
      return new Promise((resolve) => {
        getApiHost((apiHost: string) => {
          const runApiRequest = (url: string, init: RequestInit): Promise<Response> => (
            new Promise((resolveFetch, rejectFetch) => {
              fetchApiRequest(
                url,
                (fetchPromise: Promise<Response>) => {
                  fetchPromise.then(resolveFetch).catch(rejectFetch);
                },
                init,
              );
            })
          );

          const now = new Date().toISOString();
          const clipName = customName || `analysis-${now}`;
          const fileName = `${clipName.replace(/\s+/g, '-')}.wav`;
          const formData = new FormData();
          formData.append('audio_files', trimmedBlob, fileName);
          const params = new URLSearchParams();
          const trimmedSub = localExportSubpath.trim().replace(/^\/+|\/+$/g, '');
          if (trimmedSub) params.set('subpath', trimmedSub);

          runApiRequest(
            `${apiHost}/v1/export_local_wavs?${params.toString()}`,
            { method: 'POST', body: formData },
          )
            .then(async (exportRes) => {
              if (!exportRes.ok) {
                const txt = await exportRes.text();
                throw new Error(`Local save failed (${exportRes.status}): ${txt}`);
              }
              const data = await exportRes.json() as { directory?: string; files?: string[] };
              const names = (data.files || []).join(', ');
              setMessage(`WAV saved on API host under ${data.directory || '(dir)'}: ${names}`);
              resolve(true);
            })
            .catch((saveError: any) => {
              setError(saveError.message || String(saveError));
              resolve(false);
            })
            .finally(() => setUploading(false));
        });
      });
    }

    const selectedEvents = Object.entries(annotationLabels)
      .filter(([_label, checked]) => checked)
      .map(([label]) => label);
    if (selectedEvents.length === 0) {
      setError('Select at least one event before saving.');
      return false;
    }

    setUploading(true);
    return new Promise((resolve) => {
      getApiHost((apiHost: string) => {
        const runApiRequest = (url: string, init: RequestInit): Promise<Response> => (
          new Promise((resolveFetch, rejectFetch) => {
            fetchApiRequest(
              url,
              (fetchPromise: Promise<Response>) => {
                fetchPromise.then(resolveFetch).catch(rejectFetch);
              },
              init,
            );
          })
        );

        const now = new Date().toISOString();
        const clipName = customName || `analysis-${now}`;
        const fileName = `${clipName.replace(/\s+/g, '-')}.wav`;
        const uploadDevice = uploadDeviceId.trim();
        const inferredDevice = extractDeviceIdFromFileName(currentUploadFileName);
        const predictParams = new URLSearchParams();
        predictParams.set('save_audio', 'true');
        predictParams.set('lazy_saving', 'false');
        predictParams.set('no_sampling', 'true');
        if (uploadDevice) predictParams.set('device_id', uploadDevice);
        else if (inferredDevice) predictParams.set('device_id', inferredDevice);
        else predictParams.set('class_label', 'live');

        const predictFormData = new FormData();
        predictFormData.append('audio_file', trimmedBlob, fileName);

        runApiRequest(
          `${apiHost}/v1/predict?${predictParams.toString()}`,
          {
            method: 'POST',
            body: predictFormData,
          },
        )
          .then(async (predictRes) => {
            if (!predictRes.ok) {
              const txt = await predictRes.text();
              throw new Error(`Predict save failed (${predictRes.status}): ${txt}`);
            }
            const prediction = await predictRes.json();
            const predictedEventScores = Object.fromEntries(
              (prediction.events || []).map((event: any) => [event.name, event.score]),
            );
            const annotationEvents = selectedEvents.map((eventName) => [
              eventName,
              predictedEventScores[eventName] ?? 1.0,
            ]);
            const annotationPayload = {
              url: prediction.url,
              // For analysis bulk flow, class_label is used as mic/source type.
              class_label: ['safe_reaction_mic'],
              events: annotationEvents,
            };
            const annotationRes = await runApiRequest(
              `${apiHost}/v1/annotation?url=${encodeURIComponent(prediction.url)}`,
              {
                method: 'POST',
                body: JSON.stringify(annotationPayload),
                headers: { 'Content-Type': 'application/json' },
              },
            );
            if (!annotationRes.ok) {
              const txt = await annotationRes.text();
              throw new Error(`Annotation save failed (${annotationRes.status}): ${txt}`);
            }
            setMessage(`Annotated clip saved: ${prediction.url}`);
            resolve(true);
          })
          .catch((saveError: any) => {
            setError(saveError.message || String(saveError));
            resolve(false);
          })
          .finally(() => setUploading(false));
      });
    });
  };

  const saveAndLoadNextBatchFile = async () => {
    if (uploadedFiles.length === 0 || currentBatchIndex < 0) {
      setError('Save + next is only available in batch upload mode.');
      return;
    }
    const saveOk = await saveAnnotatedClip();
    if (!saveOk) return;
    const nextIndex = currentBatchIndex + 1;
    if (nextIndex < uploadedFiles.length) {
      await loadBatchFileAtIndex(nextIndex);
    } else {
      setMessage('Saved final file in batch.');
    }
  };

  return renderBoilerPlate(
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <Paper>
        <Typography variant="subtitle1">Analysis</Typography>
        <Typography variant="body2">
          Load audio from annotation event URL, upload a file, or record directly.
          Play to view a detailed rolling spectrogram and tune listening gain.
        </Typography>
        <Typography variant="body2" style={{ marginTop: '0.35rem' }}>
          <Button variant="text" size="small" component="a" href="#/analysis-batch">
            Batch view: every clip with spectrogram + play bar, range export to host folder
          </Button>
        </Typography>

        <Grid container spacing={2}>
          <Grid>
            <TextField
              label="Annotation event URL"
              value={eventUrl}
              onChange={(event) => setEventUrl(event.target.value)}
              size="small"
              style={{ minWidth: '28rem' }}
            />
            <FormHelperText>Optional: use event URL from annotation page to load source audio.</FormHelperText>
          </Grid>
          <Grid>
            <FormControl size="small" style={{ minWidth: '8rem' }}>
              <Select
                value={annotationLoadFormat}
                onChange={(event) => setAnnotationLoadFormat(event.target.value)}
              >
                <MenuItem value="mp3">mp3</MenuItem>
                <MenuItem value="wav">wav</MenuItem>
                <MenuItem value="flac">flac</MenuItem>
              </Select>
              <FormHelperText>Load format</FormHelperText>
            </FormControl>
          </Grid>
          <Grid>
            <Button variant="contained" onClick={loadFromAnnotation}>
              Load annotation audio
            </Button>
          </Grid>
        </Grid>

        <Grid container spacing={2} style={{ marginTop: '0.5rem' }}>
          <Grid>
            <Button variant="outlined" component="label">
              Upload audio file(s)
              <input type="file" hidden multiple accept="audio/*,.flac" onChange={handleFileUpload} />
            </Button>
          </Grid>
          <Grid>
            {!isRecording ? (
              <Button variant="outlined" color="secondary" onClick={startRecording}>
                Record from microphone
              </Button>
            ) : (
              <Button variant="contained" color="warning" onClick={stopRecording}>
                Stop recording
              </Button>
            )}
          </Grid>
        </Grid>
        {activeBatchFile && (
          <Grid container spacing={2} style={{ marginTop: '0.5rem' }}>
            <Grid>
              <Typography variant="body2">
                Batch file {currentBatchIndex + 1}/{uploadedFiles.length}: {activeBatchFile.name}
              </Typography>
            </Grid>
            <Grid>
              <Button
                variant="outlined"
                disabled={isLoading || currentBatchIndex <= 0}
                onClick={() => { void loadBatchFileAtIndex(currentBatchIndex - 1); }}
              >
                Previous file
              </Button>
            </Grid>
            <Grid>
              <Button
                variant="outlined"
                disabled={isLoading || currentBatchIndex >= uploadedFiles.length - 1}
                onClick={() => { void loadBatchFileAtIndex(currentBatchIndex + 1); }}
              >
                Next file
              </Button>
            </Grid>
          </Grid>
        )}
      </Paper>

      <Paper>
        <Typography variant="subtitle2">Live spectrogram and playback</Typography>
        <Typography variant="body2">Source: {sourceLabel}</Typography>
        {audioUrl && (
          <audio
            key={audioUrl}
            ref={audioRef}
            controls
            autoPlay
            preload="auto"
            src={audioUrl}
            style={{ width: '100%', maxWidth: '60rem' }}
            onPlay={async () => {
              updatePlaybackDebug('play');
              if (liveSpectrogramEnabled) {
                ensureAudioGraph();
                if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
                  await audioContextRef.current.resume();
                }
                startSpectrogram();
              }
            }}
            onPause={() => {
              stopSpectrogram();
              updatePlaybackDebug('pause');
            }}
            onEnded={() => {
              stopSpectrogram();
              updatePlaybackDebug('ended');
            }}
            onLoadedMetadata={() => updatePlaybackDebug('loadedmetadata')}
            onTimeUpdate={() => updatePlaybackDebug('timeupdate')}
            onCanPlay={() => updatePlaybackDebug('canplay')}
            onWaiting={() => updatePlaybackDebug('waiting')}
            onStalled={() => updatePlaybackDebug('stalled')}
            onSuspend={() => updatePlaybackDebug('suspend')}
            onProgress={() => updatePlaybackDebug('progress')}
            onError={() => updatePlaybackDebug('error')}
          />
        )}
        <FormControl style={{ width: '22rem', marginTop: '0.8rem' }}>
          <Typography variant="body2">Gain / amplitude ({gain.toFixed(2)}x)</Typography>
          <Slider
            min={0}
            max={10}
            step={0.05}
            value={gain}
            onChange={(_event, newValue) => setGain(asSliderValue(newValue))}
            valueLabelDisplay="auto"
          />
        </FormControl>
        <FormControl style={{ width: '22rem', marginTop: '0.2rem' }}>
          <Typography variant="body2">Noise gate ({noiseGateDb.toFixed(0)} dB)</Typography>
          <Slider
            min={-100}
            max={0}
            step={1}
            value={noiseGateDb}
            onChange={(_event, newValue) => setNoiseGateDb(asSliderValue(newValue))}
            valueLabelDisplay="auto"
          />
        </FormControl>
        <FormControl style={{ width: '22rem', marginTop: '0.2rem' }}>
          <Typography variant="body2">Low cut ({freqMinHz} Hz)</Typography>
          <Slider
            min={0}
            max={24000}
            step={50}
            value={freqMinHz}
            onChange={(_event, newValue) => {
              const sliderValue = asSliderValue(newValue);
              setFreqMinHz(Math.min(sliderValue, freqMaxHz - 50));
            }}
            valueLabelDisplay="auto"
          />
        </FormControl>
        <FormControl style={{ width: '22rem', marginTop: '0.2rem' }}>
          <Typography variant="body2">High cut ({freqMaxHz} Hz)</Typography>
          <Slider
            min={50}
            max={24000}
            step={50}
            value={freqMaxHz}
            onChange={(_event, newValue) => {
              const sliderValue = asSliderValue(newValue);
              setFreqMaxHz(Math.max(sliderValue, freqMinHz + 50));
            }}
            valueLabelDisplay="auto"
          />
        </FormControl>
        <div style={{ marginTop: '0.4rem' }}>
          <FormControlLabel
            control={(
              <Switch
                checked={liveSpectrogramEnabled}
                onChange={(_event, checked) => {
                  setLiveSpectrogramEnabled(checked);
                  if (!checked) stopSpectrogram();
                }}
                color="primary"
              />
            )}
            label="Enable live spectrogram"
          />
        </div>

        <div style={{ marginTop: '1rem' }}>
          <canvas
            ref={canvasRef}
            width={1200}
            height={320}
            style={{
              width: '100%',
              maxWidth: '74rem',
              border: '1px solid #666',
              backgroundColor: '#000',
            }}
          />
        </div>
        <Typography variant="body2" style={{ marginTop: '0.6rem', fontFamily: 'monospace' }}>
          playback debug: t={playbackDebug.currentTime.toFixed(2)} / {playbackDebug.duration.toFixed(2)}s,
          ready={playbackDebug.readyState}, network={playbackDebug.networkState},
          paused={String(playbackDebug.paused)}, ended={String(playbackDebug.ended)},
          last={playbackDebug.lastEvent}, error={playbackDebug.errorCode}
          {playbackDebug.errorMessage ? ` (${playbackDebug.errorMessage})` : ''}
        </Typography>
      </Paper>

      <Paper>
        <Typography variant="subtitle2">Detailed static spectrogram (interactive)</Typography>
        <Typography variant="body2">
          Mouse wheel zooms. Click and drag pans. This view is static (offline render), not live.
        </Typography>
        <Grid container spacing={2} style={{ marginTop: '0.5rem' }}>
          <Grid>
            <FormControl style={{ width: '14rem' }}>
              <Typography variant="body2">Time zoom ({zoomX.toFixed(2)}x)</Typography>
              <Slider
                min={1}
                max={20}
                step={0.1}
                value={zoomX}
                onChange={(_event, newValue) => setZoomX(asSliderValue(newValue))}
              />
            </FormControl>
          </Grid>
          <Grid>
            <FormControl style={{ width: '14rem' }}>
              <Typography variant="body2">Frequency zoom ({zoomY.toFixed(2)}x)</Typography>
              <Slider
                min={1}
                max={20}
                step={0.1}
                value={zoomY}
                onChange={(_event, newValue) => setZoomY(asSliderValue(newValue))}
              />
            </FormControl>
          </Grid>
          <Grid>
            <Button
              variant="contained"
              onClick={buildDetailedSpectrogram}
              disabled={!audioBuffer || isBuildingStaticSpec}
            >
              Generate detailed spectrogram
            </Button>
          </Grid>
          <Grid>
            <FormControlLabel
              control={(
                <Switch
                  checked={showBoostedDetailedSpec}
                  onChange={(_event, checked) => setShowBoostedDetailedSpec(checked)}
                  color="primary"
                />
              )}
              label={`Show boosted version (${gain.toFixed(2)}x)`}
            />
          </Grid>
          <Grid>
            <Button
              variant="outlined"
              onClick={() => {
                setZoomX(1);
                setZoomY(1);
                setPanX(0);
                setPanY(0);
              }}
            >
              Reset zoom/pan
            </Button>
          </Grid>
        </Grid>
        <div style={{ marginTop: '0.8rem' }}>
          <canvas
            ref={staticCanvasRef}
            width={1400}
            height={420}
            onMouseDown={(event) => {
              dragStateRef.current = { dragging: true, x: event.clientX, y: event.clientY };
            }}
            onMouseMove={(event) => {
              const drag = dragStateRef.current;
              if (!drag.dragging || !staticBufferCanvasRef.current || !staticCanvasRef.current) return;
              const src = staticBufferCanvasRef.current;
              const display = staticCanvasRef.current;
              const srcW = Math.max(10, Math.floor(src.width / zoomX));
              const srcH = Math.max(10, Math.floor(src.height / zoomY));
              const plotW = Math.max(
                40,
                display.width - staticSpecMargins.left - staticSpecMargins.right,
              );
              const plotH = Math.max(
                40,
                display.height - staticSpecMargins.top - staticSpecMargins.bottom,
              );
              const dx = event.clientX - drag.x;
              const dy = event.clientY - drag.y;
              dragStateRef.current = { ...drag, x: event.clientX, y: event.clientY };
              setPanX((v) => Math.max(0, Math.min(src.width - srcW, v - (dx * srcW) / plotW)));
              setPanY((v) => Math.max(0, Math.min(src.height - srcH, v - (dy * srcH) / plotH)));
            }}
            onMouseUp={() => {
              dragStateRef.current = { ...dragStateRef.current, dragging: false };
            }}
            onMouseLeave={() => {
              dragStateRef.current = { ...dragStateRef.current, dragging: false };
            }}
            style={{
              width: '100%',
              maxWidth: '76rem',
              border: '1px solid #666',
              backgroundColor: '#000',
              cursor: 'grab',
            }}
          />
        </div>
        {!hasStaticSpec && (
          <Typography variant="body2" style={{ marginTop: '0.6rem' }}>
            Detailed view is generated on demand to avoid interrupting playback.
          </Typography>
        )}
      </Paper>

      <Paper>
        <Typography variant="subtitle2">Trim and save annotated clip</Typography>
        <Typography variant="body2">Clip duration: {formatDuration(durationSec)}</Typography>
        <FormControlLabel
          style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'flex-start' }}
          control={(
            <Checkbox
              checked={saveToLocalHostFolder}
              onChange={(e) => setSaveToLocalHostFolder(e.target.checked)}
              color="primary"
            />
          )}
          label={(
            <span>
              Save trimmed WAV to folder on the API host (
              <span style={{ fontFamily: 'monospace' }}>SAFER_LOCAL_ANNOTATIONS_ROOT</span>
              {' '}or, with API{' '}
              <span style={{ fontFamily: 'monospace' }}>--debug</span>
              {' '}
              /
              <span style={{ fontFamily: 'monospace' }}> --no-cloud</span>
              , default{' '}
              <span style={{ fontFamily: 'monospace' }}>local_annotations_export/</span>
              {' '}in the repo). Skips predict/annotation upload.
            </span>
          )}
        />
        <TextField
          label="Subfolder under export root (optional)"
          size="small"
          style={{ minWidth: '20rem', maxWidth: '100%', marginTop: '0.25rem' }}
          disabled={!saveToLocalHostFolder}
          value={localExportSubpath}
          onChange={(e) => setLocalExportSubpath(e.target.value)}
          helperText="e.g. 10fpv — letters, digits, ._- per path segment only"
        />

        <Grid container spacing={2}>
          <Grid>
            <TextField
              label="Trim from start (ms)"
              type="number"
              size="small"
              value={trimStartMs}
              inputProps={{ min: MIN_TRIM_MS, max: maxTrimMs }}
              onChange={(event) => {
                setTrimStartMs(Math.max(MIN_TRIM_MS, Number(event.target.value || 0)));
              }}
            />
          </Grid>
          <Grid>
            <TextField
              label="Trim from end (ms)"
              type="number"
              size="small"
              value={trimEndMs}
              inputProps={{ min: MIN_TRIM_MS, max: maxTrimMs }}
              onChange={(event) => {
                setTrimEndMs(Math.max(MIN_TRIM_MS, Number(event.target.value || 0)));
              }}
            />
          </Grid>
          <Grid>
            <TextField
              label="Custom clip name"
              size="small"
              value={customName}
              onChange={(event) => setCustomName(event.target.value)}
            />
          </Grid>
          <Grid>
            <Typography variant="body2" style={{ marginBottom: '0.2rem' }}>
              Annotation labels
              {saveToLocalHostFolder ? ' (not used for host-folder save)' : ''}
            </Typography>
            <FormGroup row>
              {availableEvents
                .filter((event) => event.name !== 'Ignore')
                .map((event) => (
                  <FormControlLabel
                    key={event.id}
                    label={event.name}
                    control={(
                      <Checkbox
                        checked={Boolean(annotationLabels[event.name])}
                        onChange={(changeEvent) => {
                          applyAnnotationLabelChange(event.name, changeEvent.target.checked);
                        }}
                      />
                    )}
                  />
                ))}
              {customLabelNames.map((name) => (
                <span key={name} style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <FormControlLabel
                    label={name}
                    control={(
                      <Checkbox
                        checked={Boolean(annotationLabels[name])}
                        onChange={(changeEvent) => {
                          applyAnnotationLabelChange(name, changeEvent.target.checked);
                        }}
                      />
                    )}
                  />
                  <Button size="small" onClick={() => removeCustomAnnotationLabel(name)}>
                    Remove
                  </Button>
                </span>
              ))}
            </FormGroup>
            <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
              <TextField
                label="Custom event label"
                size="small"
                value={customLabelDraft}
                onChange={(e) => setCustomLabelDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCustomAnnotationLabel();
                  }
                }}
                style={{ minWidth: '14rem' }}
                disabled={saveToLocalHostFolder}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={addCustomAnnotationLabel}
                disabled={saveToLocalHostFolder}
              >
                Add label
              </Button>
            </div>
            <FormHelperText>
              Added labels behave like the built-in ones (including Casual exclusivity). Not used when saving only to the API host folder.
            </FormHelperText>
          </Grid>
          <Grid>
            <TextField
              label="Override device id (optional)"
              size="small"
              value={uploadDeviceId}
              onChange={(event) => setUploadDeviceId(event.target.value)}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} style={{ marginTop: '0.6rem' }}>
          <Grid>
            <Button variant="outlined" onClick={downloadTrimmedClip}>
              Download trimmed clip
            </Button>
          </Grid>
          <Grid>
            <Button
              variant="contained"
              color="primary"
              onClick={() => { void saveAnnotatedClip(); }}
              disabled={uploading}
            >
              {saveToLocalHostFolder ? 'Save WAV to API host' : 'Save as annotated clip'}
            </Button>
          </Grid>
          <Grid>
            <Button
              variant="contained"
              color="secondary"
              disabled={uploading || isLoading || !activeBatchFile}
              onClick={() => { void saveAndLoadNextBatchFile(); }}
            >
              Save + next file
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {isLoading && <LinearProgress />}
      {isBuildingStaticSpec && <LinearProgress />}
      {uploading && <LinearProgress />}
      {!!message && <Alert severity="success">{message}</Alert>}
      {!!error && <Alert severity="error">{error}</Alert>}
    </div>,
  );
}

export default Analysis;
